import { useEffect, useState } from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import FormRequestBlock from 'components/FormRequestBlock';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserInfo, selectUserMarginLoading, updateMarginAmount } from 'app/store/user';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { INPUT_PATTERN, trackSegmentEvent } from 'app/utils';

import './style.scss';
import { ACTION, SOURCE } from 'app/analytics';
import { canWrite } from 'app/access';

enum MarginType {
  Percent = '%',
  USD = '$',
}
const convertToMarginType = (marginIsPercent?: boolean): MarginType =>
  marginIsPercent ? MarginType.Percent : MarginType.USD;

const convertToIsPercent = (marginType: MarginType): boolean => marginType === MarginType.Percent;

const MARGIN_OPTIONS: { title: string; value: MarginType }[] = [
  { title: '% Percent', value: MarginType.Percent },
  { title: '$ USD', value: MarginType.USD },
];

const MarginBlock = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const { marginAmount, marginIsPercent } = userInfo || {};
  const canEditMargin = canWrite(userInfo, 'checkout_intents');
  const formLoading = useAppSelector(selectUserMarginLoading);
  const [inputValue, setInputValue] = useState(marginAmount);
  const [selectValue, setSelectValue] = useState(convertToMarginType(marginIsPercent));
  const dispatch = useAppDispatch();

  useEffect(() => {
    setInputValue(marginAmount);
    setSelectValue(convertToMarginType(marginIsPercent));
  }, [marginAmount, marginIsPercent]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!canEditMargin) {
      return;
    }
    dispatch(updateMarginAmount(inputValue, convertToIsPercent(selectValue)));
    trackSegmentEvent(SOURCE.MARGIN_AMOUNT_MODULE, ACTION.CLICK, 'update_button', {
      marginIsPercent: marginIsPercent,
    });
  };

  const isPercent = convertToIsPercent(selectValue);
  const selectedOption = MARGIN_OPTIONS.find((o) => o.value === selectValue) ?? MARGIN_OPTIONS[0];

  return (
    <FormRequestBlock
      title="Margin amount"
      onSubmit={handleSubmit}
      loading={formLoading}
      disabled={!canEditMargin}
      text={
        <p>Please enter the amount of margin you would like to add to the initial product price</p>
      }
    >
      <Input
        id="margin-amount"
        className="margin-input"
        type="number"
        value={inputValue ?? ''}
        placeholder="Enter amount"
        disabled={!canEditMargin}
        min={0}
        max={isPercent ? 100 : 1000000}
        step="any"
        pattern={isPercent ? INPUT_PATTERN.PERCENTS : INPUT_PATTERN.DIGITS}
        size={isPercent ? 3 : 7}
        maxLength={isPercent ? 3 : 7}
        onChange={(e) => setInputValue(Number(e.target.value))}
      />
      {/* Currency unit toggle. The DropdownMenuTrigger is the styled
          element itself — same h-10 as Input + Button so the three line
          up in the form row. */}
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Margin unit"
          disabled={!canEditMargin}
          className="inline-flex h-10 min-w-[140px] cursor-pointer items-center justify-between gap-2 border border-[var(--line-strong)] bg-[var(--input-bg)] px-3.5 font-sans text-sm text-[var(--text)] transition-[border-color,box-shadow] hover:border-[var(--text)] focus:outline-none focus-visible:border-[var(--accent)] focus-visible:ring-[3px] focus-visible:ring-[var(--ring-color)] disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-[var(--accent)]"
        >
          {selectedOption.title}
          <ChevronDownIcon className="h-4 w-4 text-[var(--text-muted)]" aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[140px]">
          {MARGIN_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => setSelectValue(opt.value)}
              data-active={opt.value === selectValue ? '' : undefined}
            >
              {opt.title}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button type="submit" variant="secondary" disabled={formLoading || !canEditMargin}>
        Save
      </Button>
    </FormRequestBlock>
  );
};

export default MarginBlock;
